import { useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { ADMIN_TASK_IDS } from "@/shared/constants/taskIds";
import { filterAnswersForTask } from "@/shared/utils/filterAnswersForTask";
import type { FlowAnswers } from "@/shared/constants/flowTypes";

const ADMIN_ANSWER_TASK_MAP: Record<string, string> = {
  // Will search
  admin_will_search_screen: ADMIN_TASK_IDS.WILL_SEARCH,
  admin_will_search_attested: ADMIN_TASK_IDS.WILL_SEARCH,
  admin_will_search_attested_at: ADMIN_TASK_IDS.WILL_SEARCH,

  // Family triage — intro
  admin_family_triage_intro: ADMIN_TASK_IDS.FAMILY_TRIAGE,

  // Family triage — marriages
  admin_family_triage_marriages: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_married_at_death: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_spouse_first_name: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_spouse_middle_name: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_spouse_last_name: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_spouse_status: ADMIN_TASK_IDS.FAMILY_TRIAGE,

  // Family triage — children
  admin_family_triage_children: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_has_children: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_children_list: ADMIN_TASK_IDS.FAMILY_TRIAGE,

  // Family triage — extended family (parents/siblings)
  admin_family_triage_extended: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_triage_review: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_triage_loading: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_has_living_parents: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_parents_list: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_has_siblings: ADMIN_TASK_IDS.FAMILY_TRIAGE,
  admin_family_siblings_list: ADMIN_TASK_IDS.FAMILY_TRIAGE,

  // Confirm admin (Step 3)
  admin_confirm_admin_reveal: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_confirm_admin_variant: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_confirm_admin_top_person_id: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_confirm_admin_who_will_serve: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_confirm_admin_mode: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_confirm_admin_eligibility: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_eligibility_age: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_eligibility_citizen: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_eligibility_felony: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_confirm_admin_co_apply: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_confirm_admin_co_applicants: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_confirm_admin_someone_else: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_someone_else_full_name: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_someone_else_relationship: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_someone_else_email: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_someone_else_phone: ADMIN_TASK_IDS.CONFIRM_ADMIN,
  admin_someone_else_note: ADMIN_TASK_IDS.CONFIRM_ADMIN,

  // Family signoffs (Step 4)
  admin_signoff_intro: ADMIN_TASK_IDS.FAMILY_SIGNOFFS,
  admin_signoff_loop: ADMIN_TASK_IDS.FAMILY_SIGNOFFS,
  admin_signoff_consents: ADMIN_TASK_IDS.FAMILY_SIGNOFFS,
  admin_signoff_loop_loading: ADMIN_TASK_IDS.FAMILY_SIGNOFFS,
  admin_signoff_loop_index: ADMIN_TASK_IDS.FAMILY_SIGNOFFS,
  admin_signoff_loop_total: ADMIN_TASK_IDS.FAMILY_SIGNOFFS,
  admin_signoff_loop_has_selection: ADMIN_TASK_IDS.FAMILY_SIGNOFFS,

  // Forms packet (Step 5)
  admin_forms_loading: ADMIN_TASK_IDS.FORMS_PACKET,
};

interface SaveAdminStepArgs {
  estateId: number;
  taskId: string;
  answers: Record<string, unknown>;
  status: "in_progress" | "completed" | "not_started";
}

interface CachedTaskRow {
  task_id: string;
  answers: Record<string, unknown> | null;
  status: string;
}

export function useAdminStepSave() {
  const { person } = useAppContext();
  const queryClient = useQueryClient();
  const estateId = person?.estate_id;
  const pendingSaveRef = useRef<Promise<unknown>>(Promise.resolve());

  const getTaskStatus = useCallback(
    (taskId: string): string | undefined => {
      const cached = queryClient.getQueryData<CachedTaskRow[]>(["estateTasks", estateId, "administration"]);
      return cached?.find((r) => r.task_id === taskId)?.status;
    },
    [queryClient, estateId],
  );

  const mutation = useMutation({
    mutationFn: async ({ estateId: eid, taskId, answers, status }: SaveAdminStepArgs) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const { data, error } = await supabase.functions.invoke("legal/probate/save-step", {
        body: { estateId: eid, taskId, answers, status },
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined,
      });

      if (error) throw new Error(error.message);
      return data;
    },
    onMutate: async (vars) => {
      const queryKey = ["estateTasks", vars.estateId, "administration"] as const;
      await queryClient.cancelQueries({ queryKey: [...queryKey] });
      const previous = queryClient.getQueryData<CachedTaskRow[]>([...queryKey]);
      if (previous) {
        queryClient.setQueryData<CachedTaskRow[]>([...queryKey],
          previous.map((row) =>
            row.task_id === vars.taskId
              ? {
                  ...row,
                  status: vars.status,
                  answers: vars.status === "not_started" ? {} : { ...(row.answers ?? {}), ...vars.answers },
                }
              : row,
          ),
        );
      }
      return { previous, queryKey: [...queryKey] };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["estateTasks", estateId] });
      queryClient.invalidateQueries({ queryKey: ["moduleProgress", estateId] });
    },
  });

  const saveStep = useCallback(
    async (
      screenId: string,
      _questionId: string | undefined,
      _answer: string | string[] | undefined,
      allAnswers: FlowAnswers,
    ) => {
      if (!estateId) return;

      const taskId = ADMIN_ANSWER_TASK_MAP[screenId];
      if (!taskId) return;

      const taskAnswers = filterAnswersForTask(allAnswers, taskId, ADMIN_ANSWER_TASK_MAP);

      const currentStatus = getTaskStatus(taskId);
      const status = currentStatus === "completed" ? "completed" : "in_progress";
      pendingSaveRef.current = pendingSaveRef.current
        .catch(() => {})
        .then(() => mutation.mutateAsync({ estateId, taskId, answers: taskAnswers, status }));
      await pendingSaveRef.current;
    },
    [estateId, mutation, getTaskStatus],
  );

  const completeTask = useCallback(
    async (taskId: string, allAnswers: FlowAnswers) => {
      if (!estateId) return;

      await pendingSaveRef.current;

      const taskAnswers = filterAnswersForTask(allAnswers, taskId, ADMIN_ANSWER_TASK_MAP);

      await mutation.mutateAsync({ estateId, taskId, answers: taskAnswers, status: "completed" });
    },
    [estateId, mutation],
  );

  const resetTaskStatus = useCallback(
    async (taskId: string) => {
      if (!estateId) return;
      await mutation.mutateAsync({ estateId, taskId, answers: {}, status: "in_progress" });
    },
    [estateId, mutation],
  );

  const resetPendingSave = useCallback(() => {
    pendingSaveRef.current = Promise.resolve();
  }, []);

  return { saveStep, completeTask, resetTaskStatus, resetPendingSave };
}
